// useAsync.js — the one data-fetching hook every screen uses.
//
// Deliberately small, and deliberately NOT a cache: this is an operations
// console, where a stale org list is worse than a slow one. Every screen shows
// what the backend said when it was last asked, plus when that was, plus a
// refresh control. Nothing revalidates behind your back.
//
// The generation counter is the important part: a superuser typing in a search
// box fires overlapping requests, and without it a slow early response can land
// after a fast later one and paint the wrong results (the classic race that
// looks like "search shows the previous query").

import React from 'react';

export function useAsync(fn, deps, { skip = false } = {}) {
  const [state, setState] = React.useState({
    data: null, error: null, loading: !skip, at: null,
  });
  const [nonce, setNonce] = React.useState(0);
  const gen = React.useRef(0);

  React.useEffect(() => {
    if (skip) { setState((s) => ({ ...s, loading: false })); return undefined; }
    const mine = ++gen.current;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    Promise.resolve()
      .then(fn)
      .then((data) => {
        if (!alive || mine !== gen.current) return;    // a newer call won
        setState({ data, error: null, loading: false, at: Date.now() });
      })
      .catch((error) => {
        if (!alive || mine !== gen.current) return;
        setState((s) => ({ ...s, error, loading: false, at: Date.now() }));
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, skip]);

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/** Debounced mirror of a value — for search boxes, so a five-letter email
 *  prefix is one request rather than five. */
export function useDebounced(value, ms = 300) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
